/**
 * Toast notification system.
 *
 * Wraps svelte-french-toast with a stable API for the canvas app.
 * The Toaster component is mounted in App.svelte at the bottom-left.
 *
 * Loading toasts (duration: 0) get a holographic glow border effect.
 *
 * Usage:
 *   import * as toast from '../lib/toast';
 *
 *   const id = toast.show('Uploading file...', { duration: 0 });
 *   toast.update(id, { message: 'Upload complete!', duration: 2000 });
 *   toast.dismiss(id);
 *
 *   toast.success('Done!');
 *   toast.error('Something broke');
 *   toast.undo('Deleted node');
 */

import frenchToast, { type Renderable } from 'svelte-french-toast';
import ArrowCounterClockwise from 'phosphor-svelte/lib/ArrowCounterClockwise';
import ArrowClockwise from 'phosphor-svelte/lib/ArrowClockwise';

// Shared style that matches the app's design system
const BASE_STYLE =
	'font-size: 0.85rem; border-radius: 10px; padding: 10px 14px; ' +
	'background: var(--panelSolid); color: var(--text); ' +
	'border: 0.36px solid var(--border); ' +
	'box-shadow: 0px 5px 6px -4px rgba(0,0,0,0.1), 0px 13px 16px -3px rgba(0,0,0,0.1);';

// Loading style — transparent border so the glow ring shows through
const LOADING_STYLE =
	'font-size: 0.85rem; border-radius: 10px; padding: 10px 14px; ' +
	'background: var(--panelSolid); color: var(--text); ' +
	'border: 0.36px solid transparent; ' +
	'box-shadow: 0px 5px 6px -4px rgba(0,0,0,0.1), 0px 13px 16px -3px rgba(0,0,0,0.1);';

/** Default auto-dismiss delay (ms) */
const DEFAULT_DURATION = 3000;

/** CSS class applied to loading toasts for the holographic glow effect */
const LOADING_CLASS = 'toast-loading';

/**
 * Show a plain toast. Returns its id so you can update/dismiss it later.
 *
 * Pass `duration: 0` for a sticky loading toast with the holographic glow.
 */
export function show(
	message: string,
	options?: { detail?: string; duration?: number },
): string {
	const duration = options?.duration ?? DEFAULT_DURATION;
	const text = options?.detail
		? `${message} \u2014 ${options.detail}`
		: message;
	const isLoading = duration === 0;

	if (isLoading) {
		return frenchToast.loading(text, {
			duration: Infinity,
			style: LOADING_STYLE,
			className: LOADING_CLASS,
			position: 'bottom-left',
		});
	}

	return frenchToast(text, {
		duration,
		style: BASE_STYLE,
		position: 'bottom-left',
	});
}

/**
 * Show a success toast.
 */
export function success(
	message: string,
	options?: { duration?: number },
): string {
	return frenchToast.success(message, {
		duration: options?.duration ?? DEFAULT_DURATION,
		style: BASE_STYLE,
		position: 'bottom-left',
	});
}

/**
 * Show an error toast.
 */
export function error(
	message: string,
	options?: { duration?: number },
): string {
	return frenchToast.error(message, {
		duration: options?.duration ?? DEFAULT_DURATION,
		style: BASE_STYLE,
		position: 'bottom-left',
	});
}

/**
 * Show a brief undo confirmation toast.
 */
export function undo(label: string): string {
	return frenchToast(label, {
		icon: ArrowCounterClockwise as unknown as Renderable,
		duration: 1500,
		style: BASE_STYLE,
		position: 'bottom-left',
	});
}

/**
 * Show a brief redo confirmation toast.
 */
export function redo(label: string): string {
	return frenchToast(label, {
		icon: ArrowClockwise as unknown as Renderable,
		duration: 1500,
		style: BASE_STYLE,
		position: 'bottom-left',
	});
}

/**
 * Update an existing toast's message (and optionally restart its timer).
 * If the toast no longer exists this is a no-op.
 *
 * When switching from loading (duration: 0) to a timed toast, the glow
 * effect is removed and a success toast replaces it.
 */
export function update(
	id: string,
	patch: { message?: string; detail?: string; duration?: number },
): void {
	const message = patch.detail
		? `${patch.message ?? ''} \u2014 ${patch.detail}`
		: (patch.message ?? '');
	const duration = patch.duration;

	// Transitioning from loading → done: use success to replace the loading spinner
	if (duration !== undefined && duration > 0) {
		frenchToast.success(message, {
			id,
			duration,
			style: BASE_STYLE,
			position: 'bottom-left',
		});
		return;
	}

	// Still loading (or no duration change)
	if (duration === 0) {
		frenchToast.loading(message, {
			id,
			duration: Infinity,
			style: LOADING_STYLE,
			className: LOADING_CLASS,
			position: 'bottom-left',
		});
		return;
	}

	frenchToast(message, {
		id,
		style: BASE_STYLE,
		position: 'bottom-left',
	});
}

/** Dismiss a toast immediately. */
export function dismiss(id: string): void {
	frenchToast.dismiss(id);
}
